import { supabase } from './supabase'

// Z73.18 Phase 1.5: builds a Descartes NA (Names & Addresses) registration CSV from na_registry
// rows -- this is the standalone master-data registration file (one row per party/premise,
// LineType='NA', full field width per CDS Names & Addresses v1.7), NOT the abbreviated 13-field
// inline NA row type a declaration itself carries (that one lives in declaration_row_schema
// under row_type='NA' and is built by cdsDeclarationExporter.ts's party references instead).
//
// Column order is read from na_row_schema (imported verbatim from the v1.7 spec's 83 fields) so
// this file never drifts from the real spec even if a later spec version reorders/adds fields --
// only the VALUE mapping below is hardcoded, to the columns na_registry actually tracks.

export interface NaRegistryRow {
  id: string
  shortname: string
  type: string
  tid: string | null
  longname: string
  street: string
  city: string
  postcode: string | null
  country: string
  vat_number: string | null
  dan: string | null
  rep_type_imports: string | null
  rep_type_exports: string | null
  rep_type_cfsp: string | null
  insurance_uplift_non_maritime: number | null
  insurance_uplift_maritime: number | null
  freight_uplift_air: number | null
  freight_uplift_sea: number | null
  freight_uplift_road: number | null
  guarantee_shortname: string | null
  aeo: string | null
  rex: string | null
  seed: string | null
  trader_excise: string | null
}

interface NaSchemaField {
  position: number
  field_slug: string
}

// Descartes' account-level client reference -- same account the declaration generator uses
// (Phase 2), since NA registration rows go through the same client account. Z73.24 P1: confirmed
// live UAT rejection ("Client ref 'UKRAD' in file does not match uploaded client 'CYUKLTD'') on
// the declaration side -- this constant had the identical wrong value, just never actually
// tested against UAT yet. Fallback only; generateNaFile below reads the real value from
// ecustoms_settings.descartes_client_ref, same source of truth as the declaration generator.
const DEFAULT_CLIENT_REF = 'CYUKLTD'

// Only the field_slugs na_registry actually stores get a value; every other of the 83 spec
// columns is emitted blank (Descartes applies its own defaults / the field simply isn't used by
// this app yet -- see na_row_schema for the full set if one becomes needed).
function valueFor(field_slug: string, row: NaRegistryRow, clientRef: string): string {
  switch (field_slug) {
    case 'LINETYPE': return 'NA'
    case 'TYPE': return row.type || ''
    case 'SHORTNAME': return row.shortname || ''
    case 'TID': return row.tid || ''
    case 'LONGNAME': return row.longname || ''
    case 'STREET': return row.street || ''
    case 'CITY': return row.city || ''
    case 'POSTCODE': return row.postcode || ''
    case 'CNTRY': return row.country || ''
    case 'TURN': return row.vat_number || ''
    case 'DAN': return row.dan || ''
    case 'CLIENTREF': return clientRef
    case 'REP_TYPE_IMPORTS': return row.rep_type_imports || ''
    case 'REP_TYPE_EXPORTS': return row.rep_type_exports || ''
    case 'REP_TYPE_CFSP': return row.rep_type_cfsp || ''
    case 'NON_MARITIME_INSURANCE_UPLIFT': return row.insurance_uplift_non_maritime != null ? String(row.insurance_uplift_non_maritime) : ''
    case 'MARITIME_INSURANCE_UPLIFT': return row.insurance_uplift_maritime != null ? String(row.insurance_uplift_maritime) : ''
    case 'FREIGHT_UPLIFT_AIR': return row.freight_uplift_air != null ? String(row.freight_uplift_air) : ''
    case 'FREIGHT_UPLIFT_SEA': return row.freight_uplift_sea != null ? String(row.freight_uplift_sea) : ''
    case 'FREIGHT_UPLIFT_ROAD': return row.freight_uplift_road != null ? String(row.freight_uplift_road) : ''
    case 'GUARANTEE': return row.guarantee_shortname || ''
    case 'AEO_AUTHORISATION': return row.aeo || ''
    case 'REX_NUMBER': return row.rex || ''
    case 'SEED_NO': return row.seed || ''
    case 'TRADER_EXCISE_NO': return row.trader_excise || ''
    default: return ''
  }
}

function csvEscape(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v
}

export async function fetchNaRowSchemaOrdered(): Promise<NaSchemaField[]> {
  const { data, error } = await supabase
    .from('na_row_schema')
    .select('position, field_slug')
    .eq('spec_version', 'v1.7')
    .order('position', { ascending: true })
  if (error) throw error
  return (data as NaSchemaField[]) || []
}

// Builds the CSV body for one or more na_registry rows against the full ordered field list.
// Per the Descartes CSV convention (same as declaration rows, Phase 2): a blank field mid-row is
// kept as an empty comma-separated cell (position matters), trailing blank cells on a row are
// trimmed since Descartes doesn't require them.
export function buildNaFileCsv(rows: NaRegistryRow[], schema: NaSchemaField[], clientRef: string = DEFAULT_CLIENT_REF): string {
  const ordered = [...schema].sort((a, b) => a.position - b.position)
  const lines = rows.map(row => {
    const cells = ordered.map(f => csvEscape(valueFor(f.field_slug, row, clientRef)))
    let lastNonBlank = -1
    for (let i = 0; i < cells.length; i++) if (cells[i] !== '') lastNonBlank = i
    return cells.slice(0, lastNonBlank + 1).join(',')
  })
  return lines.join('\r\n')
}

export async function generateNaFile(rows: NaRegistryRow[]): Promise<string> {
  const [schema, { data: settingsRow }] = await Promise.all([
    fetchNaRowSchemaOrdered(),
    supabase.from('ecustoms_settings').select('descartes_client_ref').eq('id', 'default').maybeSingle(),
  ])
  return buildNaFileCsv(rows, schema, settingsRow?.descartes_client_ref || DEFAULT_CLIENT_REF)
}

export function downloadNaFileCsv(filename: string, content: string) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
